import { z } from 'zod';
import type { AttributeCategoryToken } from '@sleed/bizcommerce-api';

/**
 * Attribute-category edit form. Field names mirror the `AttributeCategoryToken` DTO
 * (abbreviated server keys) and the validation mirrors the legacy admin grid's
 * RequiredRule/StringLengthRule (Core/client/admin/src/pages/Catalog/AttributeCategories/index.js):
 * nm required ≤100, si required.
 *
 * Deliberately omitted fields (both render as editable columns in the legacy admin grid but do NOT
 * round-trip through the API, so exposing them would silently discard input):
 *  - `cd` (Code): lives only on the ML satellite (`AttributeCategoryML.Code`), never on the base
 *    `AttributeCategory` entity. The multilanguage handler skips the ML table for the default
 *    data-entry language (`IMultilanguageDataHandler.SaveMultilanguageFields` / `ApplyMultilanguageFields`
 *    early-return when `IsActiveLanguageDefault`), so Code is never persisted or read back in `el`.
 *    Verified empirically against the local API (Insert with `cd` → GetAll returns no `cd`).
 *  - `erpcd` (ERP Code): not present on `AttributeCategoryToken` at all.
 * Both are pre-existing server-side gaps shared by the legacy admin; flagged for a follow-up.
 */
export const attributeCategorySchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int()
});

export type AttributeCategoryFormValues = z.infer<typeof attributeCategorySchema>;

export const EMPTY_ATTRIBUTE_CATEGORY: AttributeCategoryFormValues = { nm: '', iv: true, si: 0 };

export function tokenToForm(token: AttributeCategoryToken): AttributeCategoryFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		iv: token.iv ?? false,
		si: token.si ?? 0
	};
}

export function formToToken(form: AttributeCategoryFormValues): AttributeCategoryToken {
	return { id: form.id, nm: form.nm.trim(), iv: form.iv, si: form.si };
}
